<script setup>
import { ref, defineEmits, defineProps } from 'vue';

const props = defineProps({
    modelValue: {
        type: [File, String],
        default: null
    },
    id: {
        type: String,
        required: true
    },
    placeholder: {
        type: String,
        default: 'Select a .parquet file'
    }
});

const emit = defineEmits(['update:modelValue']);
const fileInputRef = ref(null);
const dragOver = ref(false);

const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const handleDrop = (e) => {
    e.preventDefault();
    dragOver.value = false;
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.parquet')) {
        emit('update:modelValue', file);
    }
};

const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
        emit('update:modelValue', file);
    }
};

const handleClick = () => {
    fileInputRef.value.click();
};

const removeFile = () => {
    emit('update:modelValue', null);
    if (fileInputRef.value) {
        fileInputRef.value.value = '';
    }
};
</script>

<template>
    <div class="file-input">
        <input
            ref="fileInputRef"
            type="file"
            :id="props.id"
            class="hidden"
            accept=".parquet"
            @change="handleFileChange"
        />
        
        <div
            class="file-input__dropzone"
            :class="{
                'file-input__dropzone--drag': dragOver,
                'file-input__dropzone--has-file': modelValue
            }"
            @click="handleClick"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop="handleDrop"
        >
            <div v-if="!modelValue" class="file-input__placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" class="file-input__icon" width="24" height="24" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                        d="M12 7v10m0-10l4 4m-4-4L8 11m11 4v3a2 2 0 01-2 2H7a2 2 0 01-2-2v-3"/>
                </svg>
                <span class="file-input__text">
                    {{ props.placeholder }}
                </span>
                <span class="file-input__subtext">
                    Drag & drop or click to select
                </span>
            </div>

            <div v-else class="file-input__file">
                <div class="file-input__file-info">
                    <svg xmlns="http://www.w3.org/2000/svg" class="file-input__file-icon" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                            d="M14 3v4a1 1 0 001 1h4"/>
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                            d="M17 21H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z"/>
                    </svg>
                    <div class="file-input__file-details">
                        <span class="file-input__filename">{{ props.modelValue.name }}</span>
                        <span class="file-input__filesize">{{ formatFileSize(props.modelValue.size) }}</span>
                    </div>
                </div>
                <button 
                    class="file-input__remove"
                    @click.stop="removeFile"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                            d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.file-input {
    @apply w-full;
}

.file-input__dropzone {
    @apply flex items-center justify-center w-full p-4 
           border-2 border-dashed border-gray-300 dark:border-gray-700 
           rounded-xl cursor-pointer
           transition-all duration-200
           bg-gray-50 dark:bg-gray-900/50;

    &:hover {
        @apply border-indigo-400 dark:border-indigo-500
               bg-indigo-50/50 dark:bg-indigo-500/5;
    }
}

.file-input__dropzone--drag {
    @apply border-indigo-500 dark:border-indigo-400
           bg-indigo-50 dark:bg-indigo-500/10;
}

.file-input__dropzone--has-file {
    @apply bg-white dark:bg-gray-800 
           border-solid border-gray-200 dark:border-gray-700;

    &:hover {
        @apply border-gray-300 dark:border-gray-600
               bg-gray-50 dark:bg-gray-700/50;
    }
}

.file-input__placeholder {
    @apply flex flex-col items-center gap-2 py-6 text-center;
}

.file-input__icon {
    @apply w-8 h-8 text-gray-400 dark:text-gray-500;
}

.file-input__text {
    @apply text-sm font-medium text-gray-700 dark:text-gray-300;
}

.file-input__subtext {
    @apply text-xs text-gray-500 dark:text-gray-400;
}

.file-input__file {
    @apply flex items-center justify-between w-full gap-4;
}

.file-input__file-info {
    @apply flex items-center gap-3 min-w-0;
}

.file-input__file-icon {
    @apply flex-shrink-0 text-indigo-500 dark:text-indigo-400;
}

.file-input__file-details {
    @apply min-w-0 flex flex-col;
}

.file-input__filename {
    @apply text-sm font-medium text-gray-900 dark:text-white truncate;
}

.file-input__filesize {
    @apply text-xs text-gray-500 dark:text-gray-400;
}

.file-input__remove {
    @apply p-1 rounded-lg text-gray-400 hover:text-gray-600 
           dark:text-gray-500 dark:hover:text-gray-300
           hover:bg-gray-100 dark:hover:bg-gray-700
           transition-colors;
}

@media (max-width: 640px) {
    .file-input__dropzone {
        @apply p-3;
    }

    .file-input__placeholder {
        @apply py-4;
    }

    .file-input__icon {
        @apply w-6 h-6;
    }
}
</style> 